import React, { useEffect, useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { IngredientCatalogDto, CatalogSource } from '@wellverse/shared-ts';
import { ExcelButtons } from '../../components/ExcelButtons';
import { API_BASE_URL, axiosInstance } from '../../providers/dataProvider';

export const IngredientCatalogList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedSource, setSelectedSource] = useState<CatalogSource | undefined>(undefined);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get<string[]>(`${API_BASE_URL}/ingredient-catalog/categories`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) setCategoryOptions(res.data);
      })
      .catch(() => {
        // Keep empty options on fetch failure.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { tableProps } = useTable<IngredientCatalogDto>({
    resource: 'ingredient-catalog',
    filters: {
      permanent: [
        { field: 'query', operator: 'contains', value: searchText },
        ...(selectedCategory ? [{ field: 'category', operator: 'eq' as const, value: selectedCategory }] : []),
        ...(selectedSource ? [{ field: 'source', operator: 'eq' as const, value: selectedSource }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="ingredient-catalog">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          placeholder="Filter by category"
          allowClear
          showSearch
          style={{ minWidth: 180 }}
          value={selectedCategory}
          onChange={(value) => setSelectedCategory(value)}
          options={categoryOptions.map((c) => ({ label: c, value: c }))}
        />
        <Select
          placeholder="Filter by source"
          allowClear
          style={{ width: 160 }}
          value={selectedSource}
          onChange={(value) => setSelectedSource(value)}
          options={Object.values(CatalogSource).map((s) => ({ label: s, value: s }))}
        />
        <ExcelButtons resource="ingredient-catalog" />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1400 }}>
        <Table.Column dataIndex="name" title="Name" fixed="left" width={180} />
        <Table.Column
          title="Translations"
          width={160}
          render={(_: any, record: IngredientCatalogDto) => {
            const t = record.translations;
            if (!t || Object.keys(t).length === 0) return '—';
            return (
              <Space wrap size={4}>
                {Object.entries(t as Record<string, any>).map(([locale, data]) => (
                  <Tag key={locale} style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>
                    {locale}: {data.name}
                  </Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column dataIndex="category" title="Category" width={110} />
        <Table.Column
          dataIndex="source"
          title="Source"
          width={100}
          render={(source: CatalogSource) => {
            const colors: Record<string, string> = {
              USDA: '#52c41a',
              CFS: '#1890ff',
              MANUAL: '#d9d9d9',
              AI_GENERATED: '#722ed1',
            };
            const c = colors[source] || '#898989';
            return (
              <Tag style={{ borderColor: c, color: c, borderRadius: 9999, padding: '2px 12px', fontWeight: 500 }}>
                {source}
              </Tag>
            );
          }}
        />
        <Table.Column dataIndex="defaultAmountGrams" title="Default (g)" width={90} />
        <Table.Column dataIndex="caloriesPer100g" title="Cal / 100g" width={100} />
        <Table.Column dataIndex="proteinPer100g" title="Protein / 100g" width={110} />
        <Table.Column dataIndex="fatPer100g" title="Fat / 100g" width={90} />
        <Table.Column dataIndex="carbsPer100g" title="Carbs / 100g" width={100} />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          width={90}
          filters={[
            { text: 'Active', value: true },
            { text: 'Inactive', value: false },
          ]}
          onFilter={(value, record: any) => record.isActive === value}
          render={(isActive: boolean) => (
            <Tag
              style={{
                borderColor: isActive ? '#3ecf8e' : '#898989',
                color: isActive ? '#3ecf8e' : '#898989',
                background: isActive ? '#3ecf8e15' : '#89898915',
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          fixed="right"
          width={120}
          render={(_, record: IngredientCatalogDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="ingredient-catalog" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="ingredient-catalog" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="ingredient-catalog"
                confirmTitle="Permanently delete this ingredient?"
                confirmOkText="Delete permanently" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
